import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useSaveUser } from "../../api/mutations/useSaveUser";
import { useSendEmail } from "../../api/mutations/useSendEmail";
import { useGetMemberships } from "../../api/queries/useGetMemberships";
import { useGetUser } from "../../api/queries/useGetUser";
import { useAppContext } from "../../Context/AppContext";
import { buildRegistrationWelcomeEmailHtml } from "../Registration/registrationWelcomeEmail";
import {
  findMembershipByName,
  formSaveIndicatesPaymentComplete,
} from "../Registration/registrationMembershipSync";
import { isYouthOrEarlyStageMembership } from "../../utils/membershipTier";
import StepsProgressBar from "../Registration/StepsProgressBar";
import MembershipStep from "../Registration/RegistrationsSteps/MemberShipStep";
import TermsStep from "../Registration/RegistrationsSteps/TermsStep";
import CheckoutStep from "../Registration/RegistrationsSteps/CheckoutStep";
import "../css/RegisterForm.css";
import { useTranslation } from "react-i18next";

interface FormData {
  membershipName: string;
  isAnnualMembership: number;
  isTermConditions: number;
  isDirectory: number;
  discountCode?: string;
}

const RenewMembership: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { objectId } = useAppContext();
  const { data: memberships } = useGetMemberships();
  const { data: userDataResponse } = useGetUser({ id: objectId || "", enabled: !!objectId });

  const [step, setStep] = useState(0);
  const [membershipPrice, setMembershipPrice] = useState(0);
  const [discountCode, setDiscountCode] = useState("");
  const [formData, setFormData] = useState<FormData>({
    membershipName: "Business Membership",
    // Only monthly memberships are active now
    isAnnualMembership: 0,
    isTermConditions: 0,
    isDirectory: 0,
  });

  const stepLabels = [
    t("membership.selectMembership"),
    t("membership.step.terms"),
    t("membership.step.payment"),
    t("membership.step.confirmation"),
  ];

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [step]);

  const user = userDataResponse?.user;

  const userDataForCheckout = useMemo(
    () =>
      user
        ? {
            ...user,
            objectId: objectId || "",
            membershipName: formData.membershipName,
            isAnnualMembership: formData.isAnnualMembership,
            discountCode,
          }
        : null,
    [
      user,
      objectId,
      formData.membershipName,
      formData.isAnnualMembership,
      discountCode,
    ]
  );

  useEffect(() => {
    if (!user) return;
    // Keep the user's membership type as default, but always use monthly billing
    setFormData((prev) => ({
      ...prev,
      membershipName: user.membershipName || prev.membershipName,
      isAnnualMembership: 0,
    }));
  }, [user]);

  useEffect(() => {
    if (step === 2 && userDataForCheckout) {
      localStorage.setItem("UserData", JSON.stringify(userDataForCheckout));
    }
  }, [step, userDataForCheckout]);

  const nextStep = () => setStep((s) => s + 1);
  const prevStep = () => setStep((s) => Math.max(s - 1, 0));

  const handleMembershipNext = (data: Partial<FormData>, price: number) => {
    setFormData((prev) => ({ ...prev, ...data }));
    setMembershipPrice(price);
    nextStep();
  };

  const handleTermsNext = (data: Partial<FormData>) => {
    setFormData((prev) => ({ ...prev, ...data }));
    nextStep();
  };

  const handleCheckoutNext = () => {
    nextStep();
  };

  const { mutate: sendMail } = useSendEmail({ onSuccess() {} });

  const { mutate: saveUserMutate, isPending: isSaveUserPending } = useSaveUser({
    onSuccess: (_data, variables) => {
      if (!formSaveIndicatesPaymentComplete(variables)) {
        return;
      }
      if (objectId) {
        queryClient.invalidateQueries({ queryKey: ["user", objectId] });
      }
      const raw = localStorage.getItem("UserData");
      const ud = raw ? JSON.parse(raw) : {};
      const firstName = typeof ud.firstName === "string" ? ud.firstName : "";
      const membershipNameSel =
        typeof ud.membershipName === "string"
          ? ud.membershipName
          : formData.membershipName;
      const { subject, fullHTML } = buildRegistrationWelcomeEmailHtml(
        firstName,
        membershipNameSel.toLowerCase(),
      );
      const em = typeof ud.email === "string" ? ud.email : "";
      if (em.trim()) {
        sendMail({
          email: em,
          subject,
          body: fullHTML,
          name: `${ud.firstName || ""} ${ud.lastName || ""}`.trim(),
        });
      }
      setStep(3);
    },
  });

  const membershipsList = useMemo(
    () =>
      Array.isArray(memberships)
        ? memberships
        : memberships
          ? [memberships]
          : [],
    [memberships],
  );

  // Disable current plan only when user has ACTIVE membership (upgrade/downgrade). When renewing after cancel, allow selecting same plan.
  const isMembershipActive =
    !!user &&
    user.status !== "InActive" &&
    user.stripeStatus?.toLowerCase() !== "canceled" &&
    user.stripeStatus?.toLowerCase() !== "cancelled";
  const currentMembershipToDisable = useMemo(() => {
    if (!user || !isMembershipActive) return undefined;
    const raw = user.membershipName?.trim();
    if (!raw) return undefined;
    const catalogMatch = findMembershipByName(membershipsList, raw);
    if (catalogMatch?.name) return catalogMatch.name;
    if (isYouthOrEarlyStageMembership(raw)) {
      const earlyStageRow = membershipsList.find((m) =>
        isYouthOrEarlyStageMembership(m.name),
      );
      if (earlyStageRow) return earlyStageRow.name;
    }
    return raw;
  }, [user, isMembershipActive, membershipsList]);

  if (!user) {
    return null;
  }

  return (
    <div className="m-4 p-4 rounded-4 border-0 shadow d-flex flex-column">
      <button
        type="button"
        className="btn btn-link p-0 mb-2 text-decoration-none align-self-start"
        onClick={() => navigate("/manage-membership")}
      >
        {t("membership.backToManage")}
      </button>
      <h4 className="mb-3">{t("membership.renewUpgradeTitle")}</h4>
      <StepsProgressBar currentStep={step} labels={stepLabels} />

      {step === 0 && (
        <MembershipStep
          key={`renew-${currentMembershipToDisable ?? "none"}-${membershipsList.length}`}
          discountCode={discountCode}
          setDiscountCode={setDiscountCode}
          previewUserId={objectId?.trim() || undefined}
          previewEmail={user.email}
          previewName={`${user.firstName} ${user.lastName}`.trim()}
          defaultValues={{
            membershipName: formData.membershipName,
            isAnnualMembership: formData.isAnnualMembership,
          }}
          memberships={membershipsList}
          setMembershipPrice={setMembershipPrice}
          onNext={handleMembershipNext}
          onBack={() => navigate("/manage-membership")}
          currentMembershipName={currentMembershipToDisable}
        />
      )}

      {step === 1 && (
        <TermsStep
          defaultValues={{
            isTermConditions: formData.isTermConditions,
            isDirectory: formData.isDirectory,
          }}
          onNext={handleTermsNext}
          onBack={prevStep}
        />
      )}

      {step === 2 && (
        <CheckoutStep
          selectedMembership={formData.membershipName}
          membershipPrice={membershipPrice}
          memberships={membershipsList}
          isAnnualMembership={formData.isAnnualMembership === 1 ? "annual" : "monthly"}
          onNext={handleCheckoutNext}
          onBack={prevStep}
          discountCode={discountCode}
          saveUserMutate={saveUserMutate}
          isSaveUserPending={isSaveUserPending}
          skipPrePaymentMembershipSync
        />
      )}

      {step === 3 && (
        <div className="p-4 text-center">
          <h5 className="text-success mb-3">{t("membership.renewSuccessTitle")}</h5>
          <p className="text-muted mb-4">
            {t("membership.renewSuccessText")}
          </p>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => navigate("/dashboard")}
          >
            {t("dashboard.goTo", { item: t("sidebar.dashboard") })}
          </button>
        </div>
      )}
    </div>
  );
};

export default RenewMembership;
